import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react'
import { api, authEvents } from '../api/client'

export interface User {
  id: string
  email: string
  name?: string
  company?: string
  createdAt: string
  role: 'user' | 'admin'
  adminRole?: string | null
}

export interface AuthContextType {
  user: User | null
  isLoading: boolean
  error: string | null
  isAdmin: boolean
  isSuperAdmin: boolean
  adminRole: string | null
  login: (email: string) => Promise<{ success: boolean; error?: string }>
  verifyMagicLink: (token: string) => Promise<{ success: boolean; error?: string }>
  logout: () => void
  refreshToken: () => Promise<boolean>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

const TOKEN_KEY = 'planscan_token'
const REFRESH_TOKEN_KEY = 'planscan_refresh_token'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch current user from API
  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) {
      setUser(null)
      return
    }

    try {
      const response = await api.get<User>('/api/auth/me')
      if (response.success && response.data) {
        setUser(response.data)
      } else {
        // Token invalid, clear it
        localStorage.removeItem(TOKEN_KEY)
        localStorage.removeItem(REFRESH_TOKEN_KEY)
        setUser(null)
      }
    } catch {
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(REFRESH_TOKEN_KEY)
      setUser(null)
    }
  }, [])

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      await fetchUser()
      setIsLoading(false)
    }

    checkAuth()
  }, [fetchUser])

  // Listen for forced logout from API client (e.g., token refresh failure)
  useEffect(() => {
    const unsubscribe = authEvents.onLogout(() => {
      setUser(null)
      setError('Your session has expired. Please sign in again.')
    })

    return () => {
      unsubscribe()
    }
  }, [])

  const login = useCallback(async (email: string) => {
    setError(null)

    try {
      const response = await api.post<{ message: string }>(
        '/api/auth/request-magic-link',
        { email }
      )

      if (response.success) {
        return { success: true }
      } else {
        const errorMsg = response.error || 'Failed to send magic link'
        setError(errorMsg)
        return { success: false, error: errorMsg }
      }
    } catch {
      const errorMsg = 'Something went wrong. Please try again.'
      setError(errorMsg)
      return { success: false, error: errorMsg }
    }
  }, [])

  const verifyMagicLink = useCallback(async (token: string) => {
    setError(null)
    setIsLoading(true)

    try {
      const response = await api.post<{
        user: User
        accessToken: string
        refreshToken: string
      }>('/api/auth/verify-magic-link', { token })

      if (response.success && response.data) {
        localStorage.setItem(TOKEN_KEY, response.data.accessToken)
        localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken)
        setUser(response.data.user)
        return { success: true }
      } else {
        const errorMsg = response.error || 'Invalid or expired link'
        setError(errorMsg)
        return { success: false, error: errorMsg }
      }
    } catch {
      const errorMsg = 'Failed to verify link. Please try again.'
      setError(errorMsg)
      return { success: false, error: errorMsg }
    } finally {
      setIsLoading(false)
    }
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    setUser(null)
  }, [])

  const refreshToken = useCallback(async () => {
    const refresh = localStorage.getItem(REFRESH_TOKEN_KEY)
    if (!refresh) return false

    try {
      const response = await api.post<{
        accessToken: string
        refreshToken: string
      }>('/api/auth/refresh-token', { refreshToken: refresh })

      if (response.success && response.data) {
        localStorage.setItem(TOKEN_KEY, response.data.accessToken)
        localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken)
        return true
      }
      return false
    } catch {
      return false
    }
  }, [])

  const refreshUser = useCallback(async () => {
    await fetchUser()
  }, [fetchUser])

  const isAdmin = user?.role === 'admin'
  const adminRole = user?.adminRole ?? null
  const isSuperAdmin = adminRole === 'SuperAdmin'

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        error,
        isAdmin,
        isSuperAdmin,
        adminRole,
        login,
        verifyMagicLink,
        logout,
        refreshToken,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
